import { Ban, Clock, KeyRound, LockKeyhole, MailWarning, UserMinus } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { SignInHealth, SignInHealthKey } from "../utils/signInHealth";

type Variant = "success" | "warning" | "danger" | "neutral" | "info";

/**
 * `ok` is the only state without an icon, deliberately: it is the majority of rows, and an icon on every one
 * would turn the column into noise and rob the six problem states of the salience that is the entire point.
 * An icon here means "look at this".
 */
const PRESENTATION: Record<SignInHealthKey, { variant: Variant; icon?: LucideIcon }> = {
  suspended: { variant: "danger", icon: Ban },
  inactive: { variant: "neutral", icon: UserMinus },
  locked: { variant: "danger", icon: LockKeyhole },
  unverified: { variant: "warning", icon: MailWarning },
  never: { variant: "info", icon: Clock },
  failing: { variant: "warning", icon: KeyRound },
  ok: { variant: "success" },
};

export interface SignInHealthCellProps {
  health: SignInHealth;
}

/**
 * The signature column: why this person can or cannot sign in, and whether anyone can do something about it.
 *
 * Built entirely from fields the listing already returns, so it costs no extra request. It never claims
 * anything about 2FA enrolment — `two_factor_secret` is `@Exclude()`d, so from the client that is a guess.
 */
export function SignInHealthCell({ health }: SignInHealthCellProps) {
  const { t } = useTranslations("users");
  const { variant, icon } = PRESENTATION[health.key];

  const label = (() => {
    switch (health.key) {
      case "suspended":
        return t("signin_suspended");
      case "inactive":
        return t("signin_inactive");
      case "locked":
        return t("signin_locked", { minutes: health.lockedMinutes });
      case "unverified":
        return t("signin_unverified");
      case "never":
        return t("signin_never");
      case "failing":
        return t("signin_failing", { count: health.failedAttempts });
      case "ok":
        return formatDateTime(health.lastLoginAt, { includeTime: false });
    }
  })();

  const title = (() => {
    switch (health.key) {
      case "suspended":
        return t("signin_suspended_title");
      case "inactive":
        return t("signin_inactive_title");
      case "locked":
        return t("signin_locked_title");
      case "unverified":
        return t("signin_unverified_title");
      case "never":
        return t("signin_never_title");
      case "failing":
        return t("signin_failing_title");
      case "ok":
        return t("signin_ok_title", {
          date: formatDateTime(health.lastLoginAt, { includeTime: true }),
        });
    }
  })();

  return (
    <ColocaStatusPill variant={variant} icon={icon} title={title} className="max-w-full">
      <span className="truncate">{label}</span>
    </ColocaStatusPill>
  );
}
